import React from 'react';
import { DETAILS_CONTENT_KEYS, IPlanContent } from '../typings/planContent.types';
import { createPlanNameMatcher, getKeyDetailsContentIcon } from './utils/PlanMetaHelper';
import {
  defaultEstimatesDialog,
  defaultKeyDetails,
  dualKeyDetailsAddons,
  electricityKeyDetailsAddons,
  gasKeyDetailsAddons,
} from './common/planDefaults';
import { List, ListItem, Stack, Text, TextLink } from '@origin-digital/ods-core';
import { BYOBattery } from './common/BYOBatteryModal';
import { InternetOfferPerk } from './common/internetOfferPerk';

const PLAN_TONE = 'promoteB';

export const gasSignupOptions = [
  'Direct debit optional for hassle-free bill payments',
  'Inbox or letterbox? Your choice for letters and bills',
];

export const defaultSignupOptions = [
  gasSignupOptions[0],
  'Your electricity billed monthly or quarterly',
  gasSignupOptions[1],
];

const solarInfoContent = (
  <Stack space="small">
    <Text>With Origin Solar Boost (with tiered feed-in tariff):</Text>
    <List space="small">
      <ListItem>your solar PV system must be net metered, and</ListItem>
      <ListItem>
        you must not be receiving a feed-in tariff (FIT) under any government scheme.
      </ListItem>
    </List>
    <Text>
      You’ll receive a higher than standard FIT until the daily export limit of 8kWh is reached. The
      daily export limit is averaged across your billing period (by multiplying the number of days
      in your billing period by the daily export limit). You’ll receive our standard retailer FIT
      (as published on our website at the time) if you export more than the daily limit and once
      your energy plan ends. FIT rates are GST inclusive (where applicable). For Victorian
      customers, the 
      <TextLink
        href="https://www.originenergy.com.au/terms-and-conditions/vic-feed-in-tariff/"
        target="_blank"
      >
        Terms and Conditions for Feed-in Tariffs - Victoria
      </TextLink>
       apply to both FITs.
    </Text>
  </Stack>
);

export const originSolarBoostContent: IPlanContent = {
  test: createPlanNameMatcher(['Origin Solar Boost']),
  content: {
    rateChangePrepositionText: {
      VIC: {
        defaultFuel:
          "Our electricity rates will change on 1 August. You'll see your new charges on your next bill after that time.",
        gas: '',
      },
      defaultState: {
        defaultFuel: '',
      },
    },
    rateDiscountText: {
      defaultState: {
        defaultFuel: 'Rates variable on a 12 month benefit period',
      },
    },
    keyFeatures: {
      defaultState: {
        defaultFuel: [
          'A great feed-in tariff (FIT) rate guaranteed for 12 months',
          'Maximise the value of your solar system',
          'Switch your plan at any time without paying any fees',
        ],
      },
    },
    signUpOptions: {
      defaultState: {
        defaultFuel: defaultSignupOptions,
        gas: gasSignupOptions,
      },
    },
    keyDetails: {
      defaultState: {
        defaultFuel: defaultKeyDetails,
      },
    },
    perkAccordion: {
      defaultState: {
        dual: [InternetOfferPerk],
        gas: [InternetOfferPerk],
        electricity: [InternetOfferPerk],
        defaultFuel: [],
      },
    },
    keyDetailsAddons: {
      defaultState: {
        defaultFuel: [],
        gas: gasKeyDetailsAddons,
        dual: [
          ...dualKeyDetailsAddons,
          {
            contentKey: DETAILS_CONTENT_KEYS.BYOBattery,
            contentValue: 'Register',
            modalContent: BYOBattery,
            // for plan card redesign in sales-monorepo only
            contentIcon: getKeyDetailsContentIcon(DETAILS_CONTENT_KEYS.BYOBattery),
            contentDescription: 'BYO battery for extra value',
          },
        ],
        electricity: [
          ...electricityKeyDetailsAddons,
          {
            contentKey: DETAILS_CONTENT_KEYS.BYOBattery,
            contentValue: 'Register',
            modalContent: BYOBattery,
            // for plan card redesign in sales-monorepo only
            contentIcon: getKeyDetailsContentIcon(DETAILS_CONTENT_KEYS.BYOBattery),
            contentDescription: 'BYO battery for extra value',
          },
        ],
      },
    },
    uiOptions: {
      defaultState: {
        defaultFuel: {
          planColor: {
            text: {
              styleGuide: 'purple',
              odsCore: PLAN_TONE,
            },
            promobox: {
              styleGuide: 'purple',
              odsCore: PLAN_TONE,
            },
            bannerText: {
              styleGuide: 'white',
              odsCore: 'white',
            },
          },
          imageUrl:
            'https://res.cloudinary.com/originenergy/image/upload/v1728624900/spot-illustrations/spot_Origin-Solar-Boost.svg',
          imageForFuelType: {
            electricity:
              'https://www.originenergy.com.au/cdn/images/originenergy/image/upload/CP/plans/cp_origin-solar-boost.svg',
            dual: 'https://www.originenergy.com.au/cdn/images/originenergy/image/upload/CP/plans/cp_origin-solar-boost.svg',
          },
        },
      },
    },
    tagLine: {
      defaultState: {
        defaultFuel: 'Get a boosted feed-in tariff',
      },
    },
    termsAndConditionsUrl: {
      defaultState: {
        defaultFuel: '/terms-and-conditions.html',
      },
    },
    estimatesDialog: defaultEstimatesDialog(),
    solarInfo: {
      defaultState: {
        defaultFuel: {
          title: 'Origin Solar Boost',
          content: solarInfoContent,
        },
      },
    },
  },
};
